/**
 * Pure decision function for the Model_Router.
 *
 * `decide(state)` answers the question "given my current per-model
 * health snapshot and clock, which model should I try next, or should
 * I wait, or should I give up?" The function is **pure** — it never
 * reads `Date.now()` or performs IO. The injected clock lives in
 * `state.nowMs`, which keeps the function deterministic and lets the
 * fast-check property tests in task 5.3 quantify over arbitrary
 * `RouterState` values.
 *
 * Behaviour mirrors the design's Property 12:
 *
 *  - If `state.forcedModel !== null` (the developer override exposed via
 *    the diagnostics view when `VITE_FORCE_MODEL_DEV === '1'`,
 *    Requirement 13.4):
 *      - If the forced model is currently eligible, return
 *        `{ kind: 'try', modelId: forcedModel }`.
 *      - Otherwise return `{ kind: 'wait', modelId: forcedModel,
 *        waitMs }`, where `waitMs` is the remaining cooldown
 *        (`max(0, cooldownUntilMs - nowMs)`). When only RPM/RPD quota
 *        is exhausted, `waitMs` collapses to `0`; the executor takes
 *        responsibility for sequencing the follow-up.
 *  - Otherwise, walk `FALLBACK_CHAIN` in declared order and return the
 *    first eligible model as `{ kind: 'try', modelId: m }`
 *    (Requirement 5.2).
 *  - If no model is eligible and no override is set, return
 *    `{ kind: 'giveUp' }`. The executor handles the
 *    "wait until the soonest cooldown then retry once" policy
 *    (Requirement 5.5) on top of this signal.
 *
 * A model `m` is *eligible* when ALL three of the following hold
 * (Requirements 5.2 and 5.10):
 *
 *   1. `state.health[m].cooldownUntilMs <= state.nowMs`
 *   2. `state.health[m].requestsLastMinute < 0.9 * state.health[m].rpmLimit`
 *   3. `state.health[m].requestsLastDay   < 0.9 * state.health[m].rpdLimit`
 *
 * Validates: Requirements 5.2, 5.10, 13.4 (Property 12).
 */

import type { ModelId, RouterDecision, RouterState } from './types';
import { FALLBACK_CHAIN } from './fallbackChain';

/**
 * Returns `true` iff `modelId` is currently eligible to receive a
 * request given `state.nowMs` and the model's tracked health.
 *
 * Preemptive-skip rule: a model is skipped when its tracked usage has
 * reached the limit (RPM or RPD). For the small daily quotas in this
 * project (e.g. 20 RPD) we use a strict `>= limit` threshold: burning
 * the last request before falling back is fine because a 429 from the
 * API sets a cooldown anyway, and stopping exactly at the known limit
 * avoids a guaranteed-to-fail call.
 *
 * If, for some reason, no `ModelHealth` entry exists for `modelId`,
 * the model is treated as ineligible. This keeps `decide` total.
 */
export function isEligible(state: RouterState, modelId: ModelId): boolean {
  const health = state.health[modelId];
  if (health === undefined) {
    return false;
  }
  if (health.cooldownUntilMs > state.nowMs) {
    return false;
  }
  if (health.requestsLastMinute >= health.rpmLimit) {
    return false;
  }
  if (health.requestsLastDay >= health.rpdLimit) {
    return false;
  }
  return true;
}

/**
 * Decides the next router action for the given state.
 *
 * Pure: reads only `state` and the static `FALLBACK_CHAIN` constant.
 */
export function decide(state: RouterState): RouterDecision {
  // Developer override path (Requirement 13.4).
  if (state.forcedModel !== null) {
    const forced = state.forcedModel;
    if (isEligible(state, forced)) {
      return { kind: 'try', modelId: forced };
    }
    const health = state.health[forced];
    const cooldownRemaining =
      health === undefined ? 0 : health.cooldownUntilMs - state.nowMs;
    const waitMs = cooldownRemaining > 0 ? cooldownRemaining : 0;
    return { kind: 'wait', modelId: forced, waitMs };
  }

  // Default path: first eligible model in FALLBACK_CHAIN order
  // (Requirement 5.2).
  for (const modelId of FALLBACK_CHAIN) {
    if (isEligible(state, modelId)) {
      return { kind: 'try', modelId };
    }
  }

  // Nothing eligible; let the executor apply the soonest-cooldown
  // retry-once policy (Requirement 5.5).
  return { kind: 'giveUp' };
}
